import { Configuration } from '../openapi';
import authService from 'src/services/auth-service';
import { createMaintenanceMiddleware } from './maintenance-interceptor';
import { createIpRestrictionMiddleware } from './ip-restriction-interceptor';
import { signOut } from './auth';

/**
 * Creates authentication middleware that adds the Bearer token to requests
 * @returns {Object} Middleware object with pre-request handler
 */
export const createAuthenticationMiddleware = () => ({
  pre: async (context) => {
    const token = await authService.idToken();
    if (!token) {
      await signOut(false);
      throw new Error("Invalid id token");
    }
    context.init.headers = {
      ...context.init.headers,
      'Authorization': `Bearer ${token}`
    };
    return context;
  }
});

/**
 * Creates a basic OpenAPI Configuration without authentication headers
 * Authentication is now handled by middleware
 * @returns {Configuration} OpenAPI Configuration
 */
export const createConfiguration = () => {
  let configuration = {};

  if (process.env.REACT_APP_BASE_API_URL) {
    configuration.basePath = process.env.REACT_APP_BASE_API_URL;
  }

  return new Configuration(configuration);
};

/**
 * Creates an instance of an API client with maintenance and IP restriction middleware
 * @template T
 * @param {new(config: Configuration) => T} ApiClass - The OpenAPI client class to instantiate
 * @returns {T} An instance of the API client with maintenance and IP restriction middleware
 */
export const createApiClient = (ApiClass) => {
  const configuration = createConfiguration();
  const apiClient = new ApiClass(configuration);

  // Add middleware (order matters - IP restriction should be checked before maintenance)
  return apiClient
    .withMiddleware(createIpRestrictionMiddleware())
    .withMiddleware(createMaintenanceMiddleware());
};

/**
 * Creates an instance of an API client with authentication and maintenance middleware
 * @template T
 * @param {new(config: Configuration) => T} ApiClass - The OpenAPI client class to instantiate
 * @returns {T} An instance of the API client with auth and maintenance middleware
 */
export const createAuthenticatedApiClient = (ApiClass) => {
  const apiClient = createApiClient(ApiClass);

  // Add authentication and maintenance middleware
  return apiClient
    .withMiddleware(createAuthenticationMiddleware());
};
